import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ticketsApi } from "../utils/api";

function useCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start || target === 0) return;
    let s = null;
    const step = (ts) => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      setCount(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const categories = ["All","Music","Comedy","Tech","Wellness","Sports","Art","Food"];
const catIcons = { Music:"🎵", Comedy:"😂", Tech:"💻", Wellness:"🧘", Sports:"⚽", Art:"🎨", Food:"🍔", All:"✨" };

export default function HomePage() {
  const [events, setEvents]   = useState([]);
  const [search, setSearch]   = useState("");
  const [cat, setCat]         = useState("All");
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded]   = useState(false);
  const [statsRef, statsInView] = useInView();
  const [howRef, howInView]     = useInView();
  const [testRef, testInView]   = useInView();

  useEffect(() => {
    setTimeout(() => setLoaded(true), 80);
    ticketsApi.getEvents()
      .then(d => setEvents(d.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const totalSold = events.reduce((a,e) => a + (parseInt(e.total_sold)||0), 0);
  const c1 = useCounter(totalSold || 250, 2000, statsInView);
  const c2 = useCounter(events.length || 10, 2000, statsInView);
  const c3 = useCounter(98, 2000, statsInView);
  const c4 = useCounter(totalSold || 200, 2000, statsInView);

  const filtered = events.filter(e => {
    const matchCat = cat === "All" || e.category === cat;
    const matchS   = !search || e.title?.toLowerCase().includes(search.toLowerCase()) || e.venue_name?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchS;
  });

  const featured = filtered.filter(e => e.featured);
  const heroBg   = featured[0]?.banner_image || events[0]?.banner_image;

  return (
    <div className="min-h-screen" style={{background:"#060010"}}>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Full-page background image */}
        {heroBg && (
          <div className="absolute inset-0">
            <img src={heroBg} alt="" className="w-full h-full object-cover opacity-25"/>
            <div className="absolute inset-0" style={{background:"linear-gradient(135deg,rgba(6,0,16,0.92) 0%,rgba(6,0,16,0.75) 50%,rgba(6,0,16,0.92) 100%)"}}/>
          </div>
        )}

        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute rounded-full blur-3xl animate-pulse"
            style={{width:"600px",height:"600px",background:"radial-gradient(circle,rgba(155,63,232,0.15),transparent)",top:"-100px",left:"-100px",animationDuration:"6s"}}/>
          <div className="absolute rounded-full blur-3xl"
            style={{width:"400px",height:"400px",background:"radial-gradient(circle,rgba(245,158,11,0.08),transparent)",bottom:"0",right:"0",animation:"pulse 8s infinite"}}/>
          {/* Grid */}
          <div className="absolute inset-0" style={{backgroundImage:"linear-gradient(rgba(155,63,232,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(155,63,232,0.04) 1px,transparent 1px)",backgroundSize:"60px 60px"}}/>
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-5" style={{backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"}}/>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-16 text-center">

          {/* Live badge */}
          <div className={`inline-flex items-center gap-2.5 mb-8 transition-all duration-700 ${loaded?"opacity-100 translate-y-0":"opacity-0 translate-y-4"}`}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
              style={{background:"rgba(155,63,232,0.15)", border:"1px solid rgba(155,63,232,0.3)", color:"#d49dff"}}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"/>
              LIVE EVENTS IN SRI LANKA
            </div>
          </div>

          {/* Main heading */}
          <h1 className={`font-black text-white mb-6 transition-all duration-700 delay-100 ${loaded?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}
            style={{fontSize:"clamp(40px,8vw,96px)", lineHeight:"1.05", letterSpacing:"-0.02em"}}>
            Experience
            <br/>
            <span style={{background:"linear-gradient(135deg,#c084fc,#a855f7,#f59e0b)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent"}}>
              Unforgettable
            </span>
            <br/>
            Moments
          </h1>

          <p className={`text-gray-400 text-base md:text-xl mb-10 max-w-lg mx-auto leading-relaxed transition-all duration-700 delay-200 ${loaded?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}>
            Concerts · Parties · Experiences across Sri Lanka.
            <br className="hidden md:block"/>
            Book via bank transfer. Get your QR on WhatsApp.
          </p>

          {/* Search bar */}
          <div className={`max-w-lg mx-auto mb-8 transition-all duration-700 delay-300 ${loaded?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}>
            <div className="relative" style={{filter:"drop-shadow(0 8px 32px rgba(155,63,232,0.2))"}}>
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input type="text" placeholder="Search events, artists, venues..."
                value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key==='Enter' && document.getElementById('events')?.scrollIntoView({behavior:'smooth'})}
                className="w-full pl-12 pr-28 py-4 rounded-2xl text-white placeholder-gray-500 text-sm focus:outline-none"
                style={{background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)", backdropFilter:"blur(20px)"}}/>
              <button onClick={() => document.getElementById('events')?.scrollIntoView({behavior:'smooth'})}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl text-white text-sm font-bold transition-all hover:opacity-90 active:scale-95"
                style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                Search
              </button>
            </div>
          </div>

          {/* Live count */}
          {events.length > 0 && (
            <p className={`text-gray-600 text-sm mb-8 transition-all duration-700 delay-400 ${loaded?"opacity-100":"opacity-0"}`}>
              <span className="text-purple-400 font-semibold">{events.length} events</span> live now
            </p>
          )}

          {/* CTAs */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-700 delay-500 ${loaded?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}>
            <a href="#events"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl text-white font-bold text-base transition-all hover:scale-105 active:scale-95"
              style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 8px 32px rgba(124,34,200,0.4)"}}>
              🎪 Browse Events
            </a>
            <Link to="/my-tickets"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl text-gray-200 font-bold text-base transition-all hover:scale-105 active:scale-95"
              style={{background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)", backdropFilter:"blur(10px)"}}>
              🎟️ My Tickets
            </Link>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-600 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-gray-700 flex items-start justify-center p-1.5">
            <div className="w-1 h-2 bg-gray-500 rounded-full animate-bounce"/>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div ref={statsRef} className="py-12 md:py-16 px-4 border-y border-white/5" style={{background:"rgba(255,255,255,0.02)"}}>
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px" style={{background:"rgba(255,255,255,0.06)", borderRadius:"20px", overflow:"hidden"}}>
          {[
            { value:c1, suffix:"+", label:"Tickets Sold", icon:"🎟️" },
            { value:c2, suffix:"+", label:"Live Events",  icon:"🎪" },
            { value:c3, suffix:"%", label:"Satisfaction", icon:"⭐" },
            { value:c4, suffix:"+", label:"WhatsApp QRs", icon:"📱" },
          ].map((s,i) => (
            <div key={i} className="p-6 md:p-8 text-center" style={{background:"#060010"}}>
              <p className="text-2xl mb-2">{s.icon}</p>
              <p className="text-2xl md:text-3xl font-black text-white">{s.value.toLocaleString()}{s.suffix}</p>
              <p className="text-gray-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURED ── */}
      {featured.length > 0 && (
        <section id="events" className="py-12 md:py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-purple-400 text-xs font-bold mb-2 uppercase tracking-widest">✦ Featured</p>
                <h2 className="text-2xl md:text-4xl font-black text-white">Don't Miss These</h2>
              </div>
              <a href="#all-events" className="text-gray-500 hover:text-white text-sm transition hidden md:block">View all →</a>
            </div>
            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              {featured.map(e => <FeaturedCard key={e.id} event={e}/>)}
            </div>
          </div>
        </section>
      )}

      {/* ── FILTER + EVENTS ── */}
      <section className="py-8 px-4" id="all-events">
        <div className="max-w-7xl mx-auto">
          {/* Filter pills */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {categories.map(c => (
              <button key={c} onClick={() => setCat(c)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                  cat===c ? "text-white scale-105" : "text-gray-400 hover:text-white"
                }`}
                style={cat===c
                  ? {background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 4px 16px rgba(124,34,200,0.3)"}
                  : {background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)"}}>
                {catIcons[c]} {c}
              </button>
            ))}
          </div>

          {/* Section header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-2xl font-black text-white">
              {cat==="All" ? "All Events" : `${cat} Events`}
              {search && <span className="text-gray-500 font-normal text-sm ml-2">for "{search}"</span>}
            </h2>
            <span className="text-gray-600 text-xs">{filtered.length} found</span>
          </div>

          {/* Event grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => (
                <div key={i} className="rounded-3xl overflow-hidden animate-pulse" style={{background:"rgba(255,255,255,0.04)"}}>
                  <div className="h-52" style={{background:"rgba(255,255,255,0.04)"}}/>
                  <div className="p-5 space-y-3">
                    <div className="h-3 rounded-full w-1/3" style={{background:"rgba(255,255,255,0.06)"}}/>
                    <div className="h-5 rounded-full w-2/3" style={{background:"rgba(255,255,255,0.06)"}}/>
                    <div className="h-3 rounded-full w-1/2" style={{background:"rgba(255,255,255,0.06)"}}/>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 rounded-3xl" style={{background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)"}}>
              <p className="text-4xl mb-4">🎭</p>
              <p className="text-white font-bold text-lg mb-2">No events found</p>
              <p className="text-gray-500 text-sm mb-4">Try a different category</p>
              {search && <button onClick={()=>setSearch("")} className="text-purple-400 text-sm hover:underline">Clear search</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filtered.map(e => <EventCard key={e.id} event={e}/>)}
            </div>
          )}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section ref={howRef} className="py-16 md:py-24 px-4 my-8 mx-4 rounded-3xl" style={{background:"rgba(155,63,232,0.05)", border:"1px solid rgba(155,63,232,0.1)"}}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-purple-400 text-xs font-bold mb-2 uppercase tracking-widest">How It Works</p>
            <h2 className="text-2xl md:text-4xl font-black text-white mb-3">Book in 4 Simple Steps</h2>
            <p className="text-gray-500 text-sm md:text-base">No account needed. No app required.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon:"🔍", title:"Browse",        desc:"Find events across Sri Lanka" },
              { icon:"🎫", title:"Pick Tickets",  desc:"Choose your tier & quantity" },
              { icon:"🏦", title:"Bank Transfer", desc:"Transfer & upload receipt" },
              { icon:"📱", title:"Get QR",        desc:"Ticket arrives on WhatsApp" },
            ].map((s,i) => (
              <div key={i} className={`text-center transition-all duration-500 ${howInView?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}
                style={{transitionDelay:`${i*100}ms`}}>
                <div className="relative w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center text-2xl"
                  style={{background:"rgba(155,63,232,0.15)", border:"1px solid rgba(155,63,232,0.25)"}}>
                  {s.icon}
                  <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs font-black text-white flex items-center justify-center"
                    style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                    {i+1}
                  </div>
                </div>
                <h3 className="text-white font-bold text-sm mb-1">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section ref={testRef} className="py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-purple-400 text-xs font-bold mb-2 uppercase tracking-widest">Reviews</p>
            <h2 className="text-2xl md:text-4xl font-black text-white">Real People, Real Experiences</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name:"Dilshan P.", location:"Colombo", text:"Got my QR ticket instantly on WhatsApp. Check-in was a breeze — just scan and go!", rating:5, event:"Neon Nights 2025", avatar:"D" },
              { name:"Malini S.",  location:"Kandy",   text:"Bought 3 tickets — each friend got their own QR. No confusion at entrance. 10/10!", rating:5, event:"Comedy Night",   avatar:"M" },
              { name:"Ruwan F.",  location:"Galle",   text:"Finally a proper ticketing platform for Sri Lanka. Easy bank transfer, worked perfectly.", rating:5, event:"Tech Summit",    avatar:"R" },
            ].map((t,i) => (
              <div key={i} className={`rounded-2xl p-5 transition-all duration-500 hover:-translate-y-1 ${testInView?"opacity-100 translate-y-0":"opacity-0 translate-y-8"}`}
                style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", transitionDelay:`${i*120}ms`}}>
                <div className="flex gap-0.5 mb-3">
                  {Array(5).fill(0).map((_,j) => <span key={j} className="text-yellow-400 text-sm">★</span>)}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-5">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/6">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                    style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">{t.name}</p>
                    <p className="text-gray-600 text-xs">{t.location} · {t.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ORGANISER CTA ── */}
      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto relative overflow-hidden rounded-3xl p-8 md:p-12 text-center"
          style={{background:"linear-gradient(135deg,#1a0040,#0d0020)", border:"1px solid rgba(155,63,232,0.2)"}}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{background:"radial-gradient(circle,#9b3fe8,transparent)"}}/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl opacity-15" style={{background:"radial-gradient(circle,#f59e0b,transparent)"}}/>
          </div>
          <p className="text-purple-400 text-xs font-bold mb-3 uppercase tracking-widest">For Organisers</p>
          <h2 className="text-2xl md:text-4xl font-black text-white mb-3">Sell Tickets Effortlessly</h2>
          <p className="text-gray-400 mb-8 text-sm md:text-base max-w-md mx-auto">Join Sri Lanka's fastest growing event platform. Set up in minutes, get paid fast.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/organiser-login"
              className="px-8 py-4 rounded-2xl text-white font-bold text-base transition-all hover:scale-105"
              style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 8px 32px rgba(124,34,200,0.35)"}}>
              Start Selling →
            </Link>
            <a href="#events" className="px-8 py-4 rounded-2xl text-gray-300 font-bold text-base transition-all hover:text-white"
              style={{background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)"}}>
              Browse Events
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}

function FeaturedCard({ event }) {
  const minPrice = event.min_price || 0;
  return (
    <Link to={`/events/${event.slug}`} className="block group relative rounded-3xl overflow-hidden"
      style={{height:"clamp(300px,38vw,460px)"}}>
      {event.banner_image
        ? <img src={event.banner_image} alt={event.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
        : <div className="w-full h-full" style={{background:"linear-gradient(135deg,#1a0030,#0d0020)"}}/>
      }
      {/* Gradient overlay */}
      <div className="absolute inset-0" style={{background:"linear-gradient(to top,rgba(0,0,0,0.97) 0%,rgba(0,0,0,0.3) 50%,transparent 100%)"}}/>
      {/* Category badge */}
      <div className="absolute top-4 left-4">
        <span className="text-xs font-bold px-3 py-1.5 rounded-full text-white"
          style={{background:"rgba(0,0,0,0.5)", backdropFilter:"blur(10px)", border:"1px solid rgba(255,255,255,0.15)"}}>
          {event.category}
        </span>
      </div>
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
        <p className="text-gray-400 text-xs mb-2">
          📅 {event.event_date ? new Date(event.event_date).toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'long',year:'numeric'}) : ''}
          &nbsp;·&nbsp;📍 {event.venue_name}
        </p>
        <h3 className="font-black text-white text-xl md:text-3xl mb-1 leading-tight group-hover:text-purple-300 transition line-clamp-2">{event.title}</h3>
        {event.subtitle && <p className="text-gray-400 text-xs md:text-sm mb-4 line-clamp-1">{event.subtitle}</p>}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-xs mb-0.5">From</p>
            <p className="text-white font-black text-xl md:text-2xl">LKR {parseInt(minPrice).toLocaleString()}</p>
          </div>
          <div className="px-5 py-2.5 rounded-2xl text-white text-sm font-bold transition-all group-hover:scale-105 group-hover:shadow-xl"
            style={{background:"linear-gradient(135deg,#166534,#16a34a)", boxShadow:"0 4px 16px rgba(22,163,74,0.3)"}}>
            Book Now →
          </div>
        </div>
      </div>
    </Link>
  );
}

function EventCard({ event }) {
  const minPrice = event.min_price || 0;
  return (
    <Link to={`/events/${event.slug}`} className="block group rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)"}}>
      <div className="relative h-48 overflow-hidden">
        {event.thumb_image || event.banner_image
          ? <img src={event.thumb_image||event.banner_image} alt={event.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"/>
          : <div className="w-full h-full flex items-center justify-center text-4xl" style={{background:"linear-gradient(135deg,#1a0030,#0d0020)"}}>🎪</div>
        }
        <div className="absolute inset-0" style={{background:"linear-gradient(to top,rgba(0,0,0,0.6) 0%,transparent 60%)"}}/>
        {/* Category */}
        <div className="absolute top-3 left-3">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full text-white"
            style={{background:"rgba(0,0,0,0.5)", backdropFilter:"blur(8px)", border:"1px solid rgba(255,255,255,0.1)"}}>
            {catIcons[event.category]} {event.category}
          </span>
        </div>
        {/* Date */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <p className="text-white text-xs font-semibold" style={{textShadow:"0 1px 4px rgba(0,0,0,0.9)"}}>
            📅 {event.event_date ? new Date(event.event_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : ''}
          </p>
          {event.start_time && (
            <p className="text-gray-300 text-xs" style={{textShadow:"0 1px 4px rgba(0,0,0,0.9)"}}>
              ⏰ {event.start_time.slice(0,5)}
            </p>
          )}
        </div>
      </div>

      <div className="p-4 md:p-5">
        <h3 className="font-bold text-white text-sm md:text-base mb-1.5 leading-snug line-clamp-2 group-hover:text-purple-300 transition">{event.title}</h3>
        <p className="text-gray-500 text-xs mb-4 line-clamp-1">📍 {event.venue_name}{event.city?`, ${event.city}`:''}</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600 text-xs">From</p>
            <p className="text-white font-black text-base">LKR {parseInt(minPrice).toLocaleString()}</p>
          </div>
          <div className="text-xs text-white font-bold px-4 py-2 rounded-xl transition-all group-hover:scale-105"
            style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
            Book Now
          </div>
        </div>
      </div>
    </Link>
  );
}
